/**
 * Productivity Dashboard Component
 * Displays comprehensive productivity metrics, ROI, and time savings
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { useProductivityMetrics } from '../hooks/useProductivityMetrics';
import { useTranslation } from 'react-i18next';
import { 
  Clock,
  DollarSign,
  TrendingUp,
  Target,
  Award,
  Calendar,
  BarChart3,
  Lightbulb,
  Gauge,
  Settings,
  Search,
  Plus
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface ProductivityDashboardProps {
  className?: string;
  showDetailedView?: boolean;
  compact?: boolean;
}

export const ProductivityDashboard: React.FC<ProductivityDashboardProps> = ({ 
  className,
  showDetailedView = false,
  compact = false 
}) => {
  const { t } = useTranslation();
  const { metrics, isLoading } = useProductivityMetrics();
  const [showConfig, setShowConfig] = useState(false);

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Target className="w-5 h-5 text-primary" />
            <span>{t('productivity.title', { ns: 'dashboard' })}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!metrics) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Target className="w-5 h-5 text-primary" />
            <span>{t('productivity.title', { ns: 'dashboard' })}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Target className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t('productivity.getStarted.title', { ns: 'dashboard' })}
            </h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
              {t('productivity.getStarted.description', { ns: 'dashboard' })}
            </p>
            
            {/* Quick Start Actions */}
            <div className="space-y-2">
              <Button 
                size="sm" 
                className="w-full justify-start"
                variant="outline"
                onClick={() => window.location.href = '/workspace'}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('productivity.getStarted.createMemory', { ns: 'dashboard' })}
              </Button>
              <Button 
                size="sm" 
                className="w-full justify-start"
                variant="outline"
                onClick={() => window.location.href = '/workspace?action=search'}
              >
                <Search className="w-4 h-4 mr-2" />
                {t('productivity.getStarted.searchMemories', { ns: 'dashboard' })}
              </Button>
            </div>

            {/* Expected Benefits Preview */}
            <div className="mt-6 pt-4 border-t border-muted">
              <p className="text-xs text-muted-foreground mb-3">
                {t('productivity.getStarted.expectedBenefits', { ns: 'dashboard' })}
              </p>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="flex items-center space-x-2">
                  <Clock className="w-3 h-3 text-green-600" />
                  <span className="text-muted-foreground">15-30 min/day saved</span>
                </div>
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-3 h-3 text-blue-600" />
                  <span className="text-muted-foreground">$50-200/month value</span>
                </div>
                <div className="flex items-center space-x-2">
                  <TrendingUp className="w-3 h-3 text-purple-600" />
                  <span className="text-muted-foreground">3x faster research</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Award className="w-3 h-3 text-orange-600" />
                  <span className="text-muted-foreground">Better decisions</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatTime = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  const getScoreColor = (score: number): string => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBadgeColor = (score: number): string => {
    if (score >= 80) return 'bg-green-100 text-green-800';
    if (score >= 60) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  if (compact) {
    return (
      <Card className={cn("bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200", className)}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-emerald-800">
            <div className="flex items-center space-x-2">
              <Target className="w-5 h-5" />
              <span>Productivity</span>
            </div>
            <Badge className={getScoreBadgeColor(metrics.productivity.score)}>
              {metrics.productivity.score}/100
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 mb-1">
                <Clock className="w-4 h-4 text-emerald-600" />
                <span className="text-lg font-bold text-emerald-700">
                  {formatTime(metrics.timeSaved.thisWeek)}
                </span>
              </div>
              <p className="text-xs text-emerald-600">Time Saved</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 mb-1">
                <DollarSign className="w-4 h-4 text-emerald-600" />
                <span className="text-lg font-bold text-emerald-700">
                  ${metrics.roi.dollarsaved}
                </span>
              </div>
              <p className="text-xs text-emerald-600">Value Created</p>
            </div>
          </div>
          
          {metrics.roi.monthlyROI > 0 && (
            <div className="text-center p-2 bg-emerald-100 rounded-lg">
              <p className="text-sm font-medium text-emerald-800">
                {metrics.roi.monthlyROI}% Monthly ROI
              </p>
              <p className="text-xs text-emerald-600">
                Pays for itself in {metrics.roi.paybackPeriod} days
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Target className="w-5 h-5 text-primary" />
            <span>Productivity Insights</span>
          </div>
          <div className="flex items-center space-x-2">
            <Badge className={getScoreBadgeColor(metrics.productivity.score)}>
              Score: {metrics.productivity.score}/100
            </Badge>
            <Button variant="ghost" size="sm" onClick={() => setShowConfig(!showConfig)}>
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border"
          >
            <div className="flex items-center justify-center space-x-1 mb-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <span className="text-2xl font-bold text-blue-700">
                {formatTime(metrics.timeSaved.thisWeek)}
              </span>
            </div>
            <p className="text-sm text-blue-600 font-medium">Time Saved This Week</p>
            <p className="text-xs text-blue-500 mt-1">
              {formatTime(metrics.timeSaved.today)} today
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border"
          >
            <div className="flex items-center justify-center space-x-1 mb-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              <span className="text-2xl font-bold text-green-700">
                ${metrics.roi.dollarsaved}
              </span>
            </div>
            <p className="text-sm text-green-600 font-medium">Value Created</p>
            <p className="text-xs text-green-500 mt-1">
              ${metrics.roi.hourlyValue}/hour rate
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-center p-4 bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg border"
          >
            <div className="flex items-center justify-center space-x-1 mb-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              <span className="text-2xl font-bold text-purple-700">
                {metrics.roi.monthlyROI > 0 ? `${metrics.roi.monthlyROI}%` : 'N/A'}
              </span>
            </div>
            <p className="text-sm text-purple-600 font-medium">Monthly ROI</p>
            <p className="text-xs text-purple-500 mt-1">
              {metrics.roi.paybackPeriod < 999 ? `${metrics.roi.paybackPeriod} day payback` : 'Building value'}
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-center p-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg border"
          >
            <div className="flex items-center justify-center space-x-1 mb-2">
              <Gauge className="w-5 h-5 text-orange-600" />
              <span className={cn("text-2xl font-bold", getScoreColor(metrics.productivity.score))}>
                {metrics.productivity.score}
              </span>
            </div>
            <p className="text-sm text-orange-600 font-medium">Productivity Score</p>
            <p className="text-xs text-orange-500 mt-1">
              {metrics.productivity.score >= 80 ? 'Excellent' : 
               metrics.productivity.score >= 60 ? 'Good' : 'Needs improvement'}
            </p>
          </motion.div>
        </div>

        {/* Productivity Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h4 className="text-sm font-medium mb-3 flex items-center space-x-2">
            <BarChart3 className="w-4 h-4 text-indigo-500" />
            <span>Productivity Breakdown</span>
          </h4>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Search Efficiency</span>
                <span className={getScoreColor(metrics.productivity.searchEfficiency)}>
                  {metrics.productivity.searchEfficiency}%
                </span>
              </div>
              <Progress value={metrics.productivity.searchEfficiency} className="h-2" />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Memory Utilization</span>
                <span className={getScoreColor(metrics.productivity.memoryUtilization)}>
                  {metrics.productivity.memoryUtilization}%
                </span>
              </div>
              <Progress value={metrics.productivity.memoryUtilization} className="h-2" />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Knowledge Velocity</span>
                <span className={getScoreColor(metrics.productivity.knowledgeVelocity)}>
                  {metrics.productivity.knowledgeVelocity}%
                </span>
              </div>
              <Progress value={metrics.productivity.knowledgeVelocity} className="h-2" />
            </div>
          </div>
        </motion.div>

        {/* Top Time Savers */}
        {metrics.insights.topTimeSavers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <h4 className="text-sm font-medium mb-3 flex items-center space-x-2">
              <Award className="w-4 h-4 text-yellow-500" />
              <span>Top Time Savers</span>
            </h4>
            <div className="space-y-2">
              {metrics.insights.topTimeSavers.slice(0, showDetailedView ? 5 : 3).map((saver, index) => (
                <motion.div
                  key={saver.activity}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium">{saver.activity}</p>
                    <p className="text-xs text-muted-foreground">{saver.description}</p>
                  </div>
                  <div className="text-right ml-3">
                    <p className="text-sm font-bold text-green-600">
                      {formatTime(saver.timeSavedMinutes)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {saver.frequency}x used
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Recommendations */}
        {metrics.insights.recommendations.length > 0 && showDetailedView && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <h4 className="text-sm font-medium mb-3 flex items-center space-x-2">
              <Lightbulb className="w-4 h-4 text-amber-500" />
              <span>Optimization Recommendations</span>
            </h4>
            <div className="space-y-2">
              {metrics.insights.recommendations.map((rec, index) => (
                <motion.div
                  key={rec.title}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="p-3 border rounded-lg"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <p className="text-sm font-medium">{rec.title}</p>
                        <Badge variant={rec.type === 'upgrade' ? 'default' : 'secondary'} className="text-xs">
                          {rec.type}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{rec.description}</p>
                    </div>
                    <div className="text-right ml-3">
                      <p className="text-sm font-medium text-blue-600">
                        +{formatTime(rec.potentialSavings)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {rec.confidence}% confidence
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Productivity Trends Chart */}
        {metrics.insights.productivityTrends.length > 0 && showDetailedView && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <h4 className="text-sm font-medium mb-3 flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-blue-500" />
              <span>7-Day Productivity Trend</span>
            </h4>
            <div className="flex items-end space-x-1 h-20 bg-muted/30 rounded-lg p-3">
              {metrics.insights.productivityTrends.map((day, index) => (
                <motion.div
                  key={day.date}
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ delay: 0.1 * index }}
                  className="flex-1 bg-gradient-to-t from-blue-500 to-blue-300 rounded-t"
                  style={{ 
                    height: `${Math.max((day.timeSaved / Math.max(...metrics.insights.productivityTrends.map(d => d.timeSaved))) * 100, 5)}%` 
                  }}
                  title={`${day.date}: ${formatTime(day.timeSaved)} saved, ${day.searchCount} searches`}
                />
              ))}
            </div>
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>7 days ago</span>
              <span>Today</span>
            </div>
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
}; 